import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { HeartPulse, Mail, Lock, ArrowRight } from 'lucide-react';
import { authService } from '../lib/auth';
import { loginSchema, type LoginFormData } from '../lib/validation';
import { getTenantSlugFromHostname } from '../lib/tenant';
import toast, { Toaster } from 'react-hot-toast';

const Login: React.FC = () => {
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const slug = getTenantSlugFromHostname();
    setTenantSlug(slug);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await authService.signIn(data.email, data.password);
      toast.success('Welcome back!');
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResetting(true);
    try {
      await authService.resetPassword(resetEmail);
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setIsResetting(false);
    }
  };

  const tenantName = tenantSlug 
    ? tenantSlug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : 'MedSystem';

  return (
    <div className="min-h-screen flex">
      <Toaster position="top-right" />
      
      {/* Left Side - Medical Background Image */}
      <div className="hidden lg:flex lg:w-3/5 relative bg-gradient-to-br from-teal-600 to-cyan-700 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-teal-600/90 to-cyan-700/90" />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center space-x-3 mb-8">
            <HeartPulse className="w-12 h-12" />
            <h1 className="text-4xl font-bold">{tenantName}</h1>
          </div>
          <h2 className="text-3xl font-semibold mb-4">
            Modern Hospital Operations, Simplified
          </h2>
          <p className="text-xl text-teal-50 leading-relaxed max-w-lg">
            Streamline patient care, manage appointments, track inventory, and access comprehensive medical records—all in one secure platform.
          </p>
          
          {/* Feature Pills */}
          <div className="mt-12 space-y-3">
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 max-w-md">
              <div className="w-2 h-2 bg-teal-300 rounded-full" />
              <span className="text-teal-50">Secure patient data management</span>
            </div>
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 max-w-md">
              <div className="w-2 h-2 bg-teal-300 rounded-full" />
              <span className="text-teal-50">Real-time appointment scheduling</span>
            </div>
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 max-w-md">
              <div className="w-2 h-2 bg-teal-300 rounded-full" />
              <span className="text-teal-50">Comprehensive billing & pharmacy</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center space-x-2 mb-8">
            <HeartPulse className="w-10 h-10 text-teal-600" />
            <h1 className="text-2xl font-bold text-gray-900">{tenantName}</h1>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            {!showForgotPassword ? (
              <>
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Welcome back
                  </h2>
                  <p className="text-gray-600">
                    Sign in to access your hospital dashboard
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        {...register('email')}
                        type="email"
                        autoComplete="email"
                        placeholder="your.email@example.com"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        {...register('password')}
                        type="password"
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                    {errors.password && (
                      <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <span>{isSubmitting ? 'Signing in...' : 'Sign in'}</span>
                    {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600">
                    New here? Use the invite link sent to your email to set up your account.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="mb-8">
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="text-sm text-gray-600 hover:text-gray-900 mb-4"
                  >
                    ← Back to sign in
                  </button>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Reset password
                  </h2>
                  <p className="text-gray-600">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handleForgotPassword}>
                  <div>
                    <label htmlFor="reset-email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="reset-email"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isResetting}
                    className="w-full flex items-center justify-center space-x-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <span>{isResetting ? 'Sending...' : 'Send reset link'}</span>
                    {!isResetting && <ArrowRight className="w-5 h-5" />}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-gray-500">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
